/**
 * **The attribute treatment's one public entry point** (LF-6388 / [ATTR-10]).
 *
 * Every consumer outside this folder imports from `@/components/attributes` and
 * never from a deep path, so splitting or renaming a module inside costs no
 * call-site churn. That is not a style preference: this system was already
 * forked once (`components/prototypes/form-system/`) precisely because its
 * modules sat under `components/leads/` behind `Lead*` names and read as
 * lead-only, and a deep-path convention would re-create the same coupling under
 * a neutral name.
 *
 * The rules a new surface needs — the closed editor union, the token rule,
 * `hidden`-not-omitted, the `onSave`-must-reject contract, and the adoption
 * ledger — are in `./CLAUDE.md`.
 */

export { AttributeGrid } from './attribute-grid';
export { AttributeChromeValue } from './attribute-chrome-value';
export {
  ATTRIBUTE_DRAWER_CLOSE_OFFSET,
  ATTRIBUTE_DRAWER_HEADER_ROW,
  AttributeDrawerDelete
} from './attribute-drawer-delete';
export { AttributeSections } from './attribute-section';
export type {
  AttributeBlock,
  AttributeComponentBlock,
  AttributeHeadingBlock,
  AttributeRowsBlock,
  AttributeSectionsProps
} from './attribute-section';
export {
  AttributeSavingIndicator,
  AttributeValueCell,
  AttributeValueSlot,
  offersClear,
  offersCopy
} from './attribute-value-cell';
export {
  AttributeUnsavedStateProvider,
  focusOpenAttributeEditor,
  escapeTargetHasOpenAttributeEditor,
  hasOpenAttributeEditor,
  useAttributeSurfaceChannel
} from './attribute-unsaved-state';
export type {
  AttributeGridControl,
  AttributeSurfaceChannel,
  AttributeSurfaceControl,
  AttributeUnsavedReporter
} from './attribute-unsaved-state';
export { AttributeFieldControl } from './attribute-field-control';
export { AttributeReadOut } from './attribute-read-out';
export {
  AttributeBooleanSegmented,
  isLiveBoolean,
  type BooleanControlSetting
} from './attribute-boolean';
export { AttributeSegmentedBoolean } from './attribute-segmented-boolean';
export { AttributeAddressEditor } from './attribute-address-editor';
export { toAddressDraft, toStructuredAddress } from './attribute-address-draft';
export { AttributeMultiSelectEditor } from './attribute-multi-select-editor';
export { AttributeNameEditor } from './attribute-name-editor';
export {
  AttributeBankAccountEditor,
  bankAccountCopyText,
  bankAccountVerifiedBadge,
  bsbDigits,
  formatBankAccountSummary,
  formatBsbInput
} from './attribute-bank-account-editor';
export { AttributePrincipalMembersEditor } from './attribute-principal-members-editor';
export {
  AttributeValueConfirmationEditor,
  AttributeValueConfirmationFields
} from './attribute-value-confirmation-editor';
export { AttributeMarketFigureEditor } from './attribute-market-figure-editor';
/*
  The field chrome, for a control this package does not itself render — the
  Contacts drawer's contact-search header, which sits on the treatment and must
  wear its ring rather than the base `Input`'s own (VALE-632). Exported here
  because §1 is "import from the index, never a deep path".
*/
export {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_EDITOR_CHROME
} from './attribute-tokens';
export {
  REFUSED_WITHOUT_REASON,
  sameDraft,
  saveFailureMessage,
  useAttributeCommit
} from './use-attribute-commit';
export type {
  AttributeCommit,
  AttributeCommitDraft,
  AttributeCommitFailure,
  AttributeCommitField,
  AttributeSiblingReader
} from './use-attribute-commit';
export {
  prefersReducedMotion,
  resolveGlide,
  useRowMotion
} from './use-row-motion';
export type { MotionRow, RowMotion, RowMotionSpec } from './use-row-motion';

export type {
  AttributeBankAccount,
  AttributeDraft,
  AttributeEditor,
  AttributeMarketFigure,
  AttributeNameParts,
  AttributeOption,
  AttributePrincipalMembers,
  AttributeSpec,
  AttributeValueConfirmation,
  AutoOpenSignal,
  BooleanControl,
  LongTextEditor
} from './attribute-types';

/**
 * **The not-recorded glyph and the empty-state text, published** (VALE-997,
 * VALE-1505). The derived `Set <label>…` text is deliberately not a per-field
 * override, a surface never gets to change *what* the placeholder says, but a
 * kept control that stands in for a row still has to render the same words.
 * Five of them were hand-typing `Set ${label}…` and reproducing the exact
 * mid-sentence capital (`Set Ownership type…`) VALE-130 removed from the grid,
 * because the function was not importable. It is now; the rule about not
 * overriding it per field stands.
 */
export {
  ATTRIBUTE_NOT_RECORDED,
  attributePlaceholder
} from './attribute-placeholder';
/**
 * **The composite-popover contract, published** (VALE-1505). Kept surfaces that
 * open a popover over an attribute row and are not one of the package's own
 * editor kinds, the inventory list's value popover, the identity-variant
 * render rows, had each hand-written the dismiss-commits / `Escape`-reverts
 * pair the package owns, and one of them had already lost `Enter`. `§3` says
 * not to write that guard again, which only holds if callers can import it.
 */
export {
  commandListToggleOnSpace,
  useAttributePopoverCommit
} from './use-attribute-popover-commit';

/**
 * **The other half of that contract, as it lands on the host** (VALE-1652). The
 * capture-phase commit above is early enough to detach the pointer's own target
 * before React dispatches, which leaves every Radix layer around the row unable
 * to tell an interaction inside it from one outside. A host that dismisses on
 * outside interactions — and above all one that guards unsaved work behind that
 * dismissal — needs to recognise the verdict it cannot trust.
 */
export {
  isUnattributedOutsideInteraction,
  type OutsideInteractionEvent
} from './attribute-outside-interaction';
export {
  AttributeCompositeField,
  AttributeCompositeMoneyField
} from './attribute-composite-fields';
export { AttributePopoverConfirm } from './attribute-popover-confirm';

export * from './attribute-tokens';
